import React, { useMemo, useState } from 'react';
import { Box, Center, Spinner, Text, VStack, Alert, AlertIcon, AlertTitle, AlertDescription, Badge, HStack, Image } from '@chakra-ui/react';
import { WaitingListEntry } from '../../services/waitingListMetrics';

interface WaitingListStaticMapProps {
  entries: WaitingListEntry[];
  selectedEntry?: WaitingListEntry;
  clusters?: Array<{
    center: { lat: number; lng: number };
    count: number;
    entries: WaitingListEntry[];
  }>;
  showClusters?: boolean;
  onToggleView?: (showClusters: boolean) => void;
}

const WaitingListStaticMap: React.FC<WaitingListStaticMapProps> = ({
  entries,
  selectedEntry,
  clusters = [],
  showClusters = true,
  onToggleView,
}) => {
  const [mapLoadError, setMapLoadError] = useState<string | null>(null);
  
  // Get Maps API key using the same logic as other map components
  const getMapsApiKey = (): string => {
    const direct = (process as any).env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
    if (direct) return direct;

    const envFromStorage = typeof window !== 'undefined' ? (localStorage.getItem('selectedEnvironment') || '').toLowerCase() : '';
    const reactAppEnv = ((process as any).env.REACT_APP_ENV || '').toLowerCase();
    const nodeEnv = ((process as any).env.NODE_ENV || '').toLowerCase();
    
    let buildEnv = envFromStorage || reactAppEnv;
    if (!buildEnv && nodeEnv === 'production') {
      buildEnv = 'production';
    } else if (!buildEnv) {
      buildEnv = 'development';
    }
    
    const envName = buildEnv.toLowerCase();
    const byEnv: Record<string, string | undefined> = {
      development: (process as any).env.REACT_APP_GOOGLE_MAPS_API_KEY_DEV,
      preview: (process as any).env.REACT_APP_GOOGLE_MAPS_API_KEY_PREVIEW,
      production: (process as any).env.REACT_APP_GOOGLE_MAPS_API_KEY_PROD,
    };
    
    return byEnv[envName] || '';
  };

  // Build static map URL
  const staticMapUrl = useMemo(() => {
    const apiKey = getMapsApiKey();
    if (!apiKey) return null;

    const validEntries = entries.filter(e => e.latitude && e.longitude);
    if (validEntries.length === 0) return null;

    // Build markers array and determine what we're actually displaying
    const markerParams: string[] = [];
    let displayLats: number[] = [];
    let displayLngs: number[] = [];
    
    if (showClusters && clusters.length > 0) {
      // Show cluster centers - calculate center from cluster positions
      clusters.forEach((cluster, index) => {
        const color = index === 0 ? 'red' : index === 1 ? 'orange' : index === 2 ? 'yellow' : 'blue';
        // Format: color:red|label:5|lat,lng
        markerParams.push(`color:${color}|label:${cluster.count}|${cluster.center.lat},${cluster.center.lng}`);
        displayLats.push(cluster.center.lat);
        displayLngs.push(cluster.center.lng);
      });
    } else {
      // Show individual entries - calculate center from entry positions
      validEntries.forEach((entry) => {
        const isSelected = selectedEntry?.id === entry.id;
        const color = isSelected ? 'green' : 'orange';
        const size = isSelected ? 'mid' : 'small';
        // Format: color:orange|size:small|lat,lng
        markerParams.push(`color:${color}|size:${size}|${entry.latitude},${entry.longitude}`);
        displayLats.push(entry.latitude!);
        displayLngs.push(entry.longitude!);
      });
    }

    // Calculate bounds based on what we're actually displaying
    if (displayLats.length === 0) return null;
    
    const minLat = Math.min(...displayLats);
    const maxLat = Math.max(...displayLats);
    const minLng = Math.min(...displayLngs);
    const maxLng = Math.max(...displayLngs);
    
    // Add padding to bounds (5% on each side)
    const latPadding = (maxLat - minLat) * 0.05;
    const lngPadding = (maxLng - minLng) * 0.05;
    
    const paddedMinLat = minLat - latPadding;
    const paddedMaxLat = maxLat + latPadding;
    const paddedMinLng = minLng - lngPadding;
    const paddedMaxLng = maxLng + lngPadding;
    
    const centerLat = (paddedMinLat + paddedMaxLat) / 2;
    const centerLng = (paddedMinLng + paddedMaxLng) / 2;

    // Calculate zoom based on bounds (more accurate calculation)
    const latDiff = paddedMaxLat - paddedMinLat;
    const lngDiff = paddedMaxLng - paddedMinLng;
    const maxDiff = Math.max(latDiff, lngDiff);
    
    // More precise zoom calculation
    let zoom = 10;
    if (maxDiff > 10) zoom = 4;
    else if (maxDiff > 5) zoom = 5;
    else if (maxDiff > 2) zoom = 6;
    else if (maxDiff > 1) zoom = 7;
    else if (maxDiff > 0.5) zoom = 8;
    else if (maxDiff > 0.2) zoom = 9;
    else if (maxDiff > 0.1) zoom = 10;
    else if (maxDiff > 0.05) zoom = 11;
    else if (maxDiff > 0.02) zoom = 12;
    else if (maxDiff > 0.01) zoom = 13;
    else zoom = 14;

    // Build the static map URL
    const baseUrl = 'https://maps.googleapis.com/maps/api/staticmap';
    const params = new URLSearchParams({
      center: `${centerLat},${centerLng}`,
      zoom: zoom.toString(),
      size: '800x500',
      scale: '2', // For retina displays
      maptype: 'roadmap',
      key: apiKey,
    });

    // Add each marker as a separate parameter
    markerParams.forEach(marker => {
      params.append('markers', marker);
    });

    const finalUrl = `${baseUrl}?${params.toString()}`;
    
    // Debug logging (remove in production if needed)
    if (process.env.NODE_ENV === 'development') {
      console.log('🗺️ Static Map URL:', finalUrl);
      console.log('📍 Markers:', markerParams.length, markerParams);
      console.log('🎯 Center:', centerLat, centerLng, 'Zoom:', zoom);
    }
    
    return finalUrl;
  }, [entries, selectedEntry, clusters, showClusters]);

  const apiKey = getMapsApiKey();
  if (!apiKey) {
    return (
      <Alert status="warning">
        <AlertIcon />
        Google Maps API key not configured
      </Alert>
    );
  }

  const validEntries = entries.filter(e => e.latitude && e.longitude);
  if (validEntries.length === 0) {
    return (
      <Center h="500px" bg="gray.50" borderRadius="md">
        <VStack>
          <Text color="gray.500">No entries with valid coordinates</Text>
          <Text fontSize="sm" color="gray.400">
            Waiting list entries need latitude and longitude to display on map
          </Text>
        </VStack>
      </Center>
    );
  }

  if (!staticMapUrl) {
    return (
      <Center h="500px" bg="gray.50" borderRadius="md">
        <Spinner size="xl" color="blue.500" />
      </Center>
    );
  }

  return (
    <VStack spacing={2} align="stretch">
      <HStack>
        <Text fontSize="sm" fontWeight="medium">View:</Text>
        <Badge
          as="button"
          colorScheme={showClusters ? 'blue' : 'gray'}
          cursor="pointer"
          onClick={() => onToggleView?.(true)}
        >
          Clusters ({clusters.length})
        </Badge>
        <Badge
          as="button"
          colorScheme={!showClusters ? 'blue' : 'gray'}
          cursor="pointer"
          onClick={() => onToggleView?.(false)}
        >
          Individual ({validEntries.length})
        </Badge>
      </HStack>
      <Box
        w="100%"
        h="500px"
        borderRadius="md"
        overflow="hidden"
        border="1px solid"
        borderColor="gray.200"
        position="relative"
        bg="gray.50"
      >
        {mapLoadError ? (
          <Center h="100%" bg="gray.50">
            <VStack spacing={3} p={4}>
              <Alert status="error" borderRadius="md" maxW="400px">
                <AlertIcon />
                <VStack align="start" spacing={2}>
                  <AlertTitle>Map failed to load</AlertTitle>
                  <AlertDescription fontSize="sm">
                    The Google Static Maps API may not be enabled for this API key.
                    <br />
                    <Text as="span" fontWeight="bold">To fix:</Text>
                    <br />
                    1. Go to Google Cloud Console → APIs & Services → Library
                    <br />
                    2. Search for "Maps Static API"
                    <br />
                    3. Click "Enable"
                    <br />
                    4. Verify the API key has proper restrictions
                  </AlertDescription>
                </VStack>
              </Alert>
              <Text fontSize="xs" color="gray.500" textAlign="center">
                API Key: {apiKey ? `${apiKey.substring(0, 10)}...` : 'Not configured'}
              </Text>
            </VStack>
          </Center>
        ) : (
          <Image
            src={staticMapUrl}
            alt="Waiting list entries map"
            w="100%"
            h="100%"
            objectFit="contain"
            bg="gray.50"
            onError={(e) => {
              const errorMsg = 'Failed to load static map. The Maps Static API may not be enabled.';
              console.error('🗺️ Static Map Error:', errorMsg);
              console.error('📋 URL:', staticMapUrl);
              console.error('🔑 API Key:', apiKey ? `${apiKey.substring(0, 10)}...` : 'Not configured');
              setMapLoadError(errorMsg);
            }}
            onLoad={() => {
              setMapLoadError(null);
              if (process.env.NODE_ENV === 'development') {
                console.log('✅ Static map loaded successfully');
              }
            }}
          />
        )}
        <Box
          position="absolute"
          bottom={2}
          right={2}
          bg="white"
          px={2}
          py={1}
          borderRadius="md"
          fontSize="xs"
          color="gray.600"
          boxShadow="sm"
        >
          <Text>Powered by Google Maps</Text>
        </Box>
      </Box>
      <Text fontSize="xs" color="gray.500" textAlign="center">
        {showClusters
          ? `Showing ${clusters.length} geographic clusters`
          : `Showing ${validEntries.length} individual entries`}
      </Text>
    </VStack>
  );
};

export default WaitingListStaticMap;

